import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { toZonedTime } from "date-fns-tz";

import { Chat } from "../Chat/Chat";
import { EnterYourData } from "./EnterYourData";
import { CollapsedChat } from "./CollapsedChat";
import { LeaveDialogs } from "./LeaveDialogs";
import MainStyled from "./MainStyled";

import { getDayName } from "../../utils";
import { loadGoogleFont } from "../../utils/loadGoogleFont";
import { useIframePositioning } from "../../utils/useIframePositioning";

import { useChatLifecycle } from "../../hooks/useChatLifecycle";
import { useDialogSocketHandlers } from "../../hooks/useDialogSocketHandlers";
import { useResponsive } from "../../hooks/useResponsive";

import { useChat } from "../../context/Context";
import { initAnalytics } from "../../utils/initAnalytics";
import { initFacebookPixel } from "../../utils/initFacebookPixel";
import { emitEventGA } from "../../utils/emitEventGA";

const isSettingEnabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const Main = () => {
  const {
    dialogId,
    isDialogActive,
    isDialogStateReady,
    setDialogActive,
    messages,
    socketInstance,
    widgetData,
    showWidget,
    hideChatButton,
    hideChatButtonWithoutGetClient,
    engineAutomations,
    defaultLang,
  } = useChat();

  const {
    font: currentFont,
    timeout,
    position,
    rounding,
    margins = [10, 10],
    timezone,
    schedule,
    channels,
    integrations,
    ga4_code,
    facebook_pixel_code,
    visitor_data_request,
    visitor_messages,
    evaluation,
    one_click_close,
  } = widgetData || {};

  const hideRef = useRef(false);

  useEffect(() => {
    hideRef.current = !!hideChatButton;
  }, [hideChatButton]);

  const [isWidgetShown, setWidgetShown] = useState(true);
  const [isLeaveDialogBlockShown, setLeaveDialogBlockShown] = useState(false);
  const [isRequestpersonalDataShown, setRequestpersonalDataShown] =
    useState(false);
  const [isAddYourDataVisible, setIsAddYourDataVisible] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [isChatActive] = useState(false);
  const [loadIntegrations, setLoadIntegrations] = useState([]);
  const [lastOperatorMessage, setLastOperatorMessage] = useState(null);

  const analyticsGA = useRef(false);
  const facebookPixel = useRef(false);
  const isSmallScreen = useResponsive();

  const {
    fetchDialogId,
    setDialogData,
    isPreload,
    setPreload,
    preloadMessagesHandler,
    sendMessage,
    messagesRequest,
    isLoading,
    messagesData,
    closeDialog,
  } = useChatLifecycle();

  const hasScriptWithSamePath = (doc, urlKey) => {
    const nodes = doc.getElementsByTagName("script");
    for (let i = 0; i < nodes.length; i++) {
      const s = nodes[i];
      if (!s.src) continue;
      try {
        const u = new URL(s.src, doc.baseURI);
        const key = u.origin + u.pathname;
        if (key === urlKey) return true;
      } catch {}
    }
    return false;
  };

  useEffect(() => {
    if (!integrations?.length && !ga4_code) return;

    if (ga4_code && !analyticsGA.current) {
      initAnalytics(ga4_code);

      analyticsGA.current = true;
    }

    if (facebook_pixel_code && !facebookPixel.current) {
      initFacebookPixel(facebook_pixel_code);

      facebookPixel.current = true;
    }

    const targetDoc = (() => {
      try {
        return window.parent?.document || document;
      } catch {
        return document;
      }
    })();

    const loadedSet = (targetDoc.__getclientLoadedKeys =
      targetDoc.__getclientLoadedKeys || new Set());

    integrations.forEach(({ type, script }) => {
      // const script = `<script async src="http://widget-get-client/widget_script.js?offset=80&pos=bottom-right&xPadding=40&yPadding=40" type="text/javascript" charset="UTF-8"></script>`;

      if (type !== "getclient" || !script) return;

      const match = script.match(/src=["']([^"']+)["']/i);
      if (!match) return;

      let src = match[1];
      try {
        const u = new URL(src, targetDoc.baseURI);

        if (facebook_pixel_code) {
          u.searchParams.set("pixelId", facebook_pixel_code);
        }

        const urlKey = u.origin + u.pathname;
        if (loadedSet.has(urlKey) || hasScriptWithSamePath(targetDoc, urlKey))
          return;

        const el = targetDoc.createElement("script");
        el.src = u.href;
        el.async = true;
        el.charset = "UTF-8";
        el.dataset.streamcrm = "getclient";
        el.dataset.urlKey = urlKey;

        let integrationReady = false;

        const handleReady = (e) => {
          if (e?.data?.type !== "getclient:ready") return;

          integrationReady = true;
          window.removeEventListener("message", handleReady);

          setLoadIntegrations((prev) =>
            prev.includes("getclient") ? prev : [...prev, "getclient"],
          );

          if (hideRef.current) {
            const target =
              window.parent && window.parent !== window
                ? window.parent
                : window;
            try {
              target.postMessage({ type: "getclient:hide", payload: {} }, "*");
            } catch (err) {
              console.error("Failed to hide chat button (from ready)", err);
            }
          }
        };

        window.addEventListener("message", handleReady);

        el.onload = () => {
          loadedSet.add(urlKey);
          setTimeout(() => {
            if (!integrationReady) {
              console.warn(
                "[Widget] getclient ready not received, forcing as loaded",
              );
              setLoadIntegrations((prev) => [...prev, "getclient"]);
            }
          }, 2000);
        };

        el.onerror = () => console.error("Failed to load widget", u.href);
        (
          targetDoc.body ||
          targetDoc.head ||
          targetDoc.documentElement
        ).appendChild(el);
      } catch (e) {
        console.error("Bad widget src:", src, e);
      }
    });
  }, [integrations, ga4_code, facebook_pixel_code]);

  useEffect(() => {
    const hasGetClient = loadIntegrations.includes("getclient");

    if (!hideChatButton) return;
    if (!hasGetClient) return;

    const target =
      window.parent && window.parent !== window ? window.parent : window;
    try {
      target.postMessage({ type: "getclient:hide", payload: {} }, "*");
    } catch (e) {
      console.error("Failed to hide chat button (from effect)", e);
    }
  }, [hideChatButton, loadIntegrations]);

  useDialogSocketHandlers({
    socketInstance,
    dialogId,
    fetchDialogId,
    messagesRequest,
    setDialogActive,
    setIsAddYourDataVisible,
    setData: setDialogData,
    isWidgetShown,
    setIsOnline,
    onOperatorMessage: setLastOperatorMessage,
  });

  useEffect(() => {
    if (currentFont) loadGoogleFont(currentFont);
  }, [currentFont]);

  useEffect(() => {
    if (!timeout) {
      setIsTimeout(true);
      return;
    }
    const timer = setTimeout(() => setIsTimeout(true), timeout * 1000);
    return () => clearTimeout(timer);
  }, [timeout]);

  const quickMessages = useMemo(() => {
    if (!isDialogStateReady || lastOperatorMessage || !Array.isArray(visitor_messages)) return [];

    return visitor_messages
      .filter(({ active, button, message }) => active && button?.[defaultLang] && message?.[defaultLang])
      .slice(0, 3)
      .map(({ button, message }) => ({
        button: button[defaultLang],
        message: message[defaultLang],
      }));
  }, [defaultLang, lastOperatorMessage, isDialogStateReady, visitor_messages]);

  useIframePositioning({
    isWidgetShown,
    isSmallScreen,
    showWidget,
    position,
    radius: rounding?.window,
    margins,
  });

  const isInSchedule = useMemo(() => {
    const currentDate = new Date();
    const hasTimezone = typeof timezone === "string" && timezone.trim();
    const zonedDate = hasTimezone
      ? toZonedTime(currentDate, timezone)
      : currentDate;
    const scheduleDay = getDayName(zonedDate);
    const daySchedule = schedule?.[scheduleDay];

    if (!daySchedule?.active) return false;

    const parseTime = (value) => {
      if (!value) return null;
      const [hPart = "0", mPart = "0"] = String(value).split(":");
      const hour = Number.isFinite(Number(hPart)) ? Number(hPart) : 0;
      const minute = Number.isFinite(Number(mPart)) ? Number(mPart) : 0;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
      return hour * 60 + minute;
    };

    const startMinutes = parseTime(daySchedule.start);
    const endMinutes = parseTime(daySchedule.end);
    if (typeof startMinutes !== "number" || typeof endMinutes !== "number")
      return false;

    const currentMinutes = zonedDate.getHours() * 60 + zonedDate.getMinutes();

    if (startMinutes < endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }, [schedule, timezone]);

  const hideYourDataHandler = useCallback(() => {
    setIsAddYourDataVisible((prev) => !prev);
    setRequestpersonalDataShown((prev) => !prev);
  }, []);

  const closeDialogHandler = useCallback(() => {
    closeDialog({ method: "POST" }).then(() => {
      messagesRequest();
      setDialogActive(false);
      setLeaveDialogBlockShown(false);
      setWidgetShown(true);
      setLastOperatorMessage(null);
      localStorage.setItem("dialogStatus", "closed");
    });
  }, [closeDialog, messagesRequest]);

  const evaluationEnabled = isSettingEnabled(evaluation);
  const oneClickCloseEnabled = isSettingEnabled(one_click_close);

  const closeRequestHandler = useCallback(() => {
    if (oneClickCloseEnabled && !evaluationEnabled) {
      closeDialogHandler();
      return;
    }

    setLeaveDialogBlockShown(true);
  }, [closeDialogHandler, evaluationEnabled, oneClickCloseEnabled]);

  const toggleChat = useCallback(() => {
    if (isPreload) setPreload(false);
    if (isWidgetShown) setLastOperatorMessage(null);
    setWidgetShown((prev) => !prev);
  }, [isPreload, isWidgetShown]);

  const openChat = useCallback(() => {
    if (isPreload) setPreload(false);
    setLastOperatorMessage(null);
    setWidgetShown(false);
  }, [isPreload]);

  const sendQuickMessage = useCallback(
    async (text) => {
      openChat();
      await sendMessage(text);
    },
    [openChat, sendMessage],
  );

  useEffect(() => {
    const handleOpenRequestForm = () => {
      setIsAddYourDataVisible(true);
    };

    window.addEventListener(
      "streamchat:open-request-form",
      handleOpenRequestForm,
    );

    return () => {
      window.removeEventListener(
        "streamchat:open-request-form",
        handleOpenRequestForm,
      );
    };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      await fetchDialogId({ method: "POST" });
    };

    window.addEventListener("streamchat:fetch-dialog", fetch);

    return () => {
      window.removeEventListener("streamchat:fetch-dialog", fetch);
    };
  }, [toggleChat]);

  useEffect(() => {
    if (!isWidgetShown) {
      emitEventGA("chat_requested", {}, ga4_code);
    }
  }, [isWidgetShown]);

  const prevWidgetShown = useRef(isWidgetShown);
  useEffect(() => {
    if (!prevWidgetShown.current && isWidgetShown) {
      engineAutomations?.notifyChatClosed();
    }
    prevWidgetShown.current = isWidgetShown;
  }, [isWidgetShown]);

  const membersData = useMemo(() => {
    return messagesData?.data?.status === "unresolved"
      ? messagesData?.data?.chat?.members
      : [];
  }, [messagesData]);

  if (!isTimeout) return null;

  if (hideChatButton || hideChatButtonWithoutGetClient) return null;

  return !isWidgetShown ? (
    <MainStyled
      isRequestpersonalDataShown={isRequestpersonalDataShown}
      isLeaveDialogBlockShown={isLeaveDialogBlockShown}
      isSmallScreen={isSmallScreen}
      position={position}
      margins={margins}
      disableGutters
    >
      {isLeaveDialogBlockShown ? (
        <LeaveDialogs
          evaluationEnabled={evaluationEnabled}
          oneClickCloseEnabled={oneClickCloseEnabled}
          showChatHandler={() => setLeaveDialogBlockShown(false)}
          hideChatHandler={closeDialogHandler}
        />
      ) : !isRequestpersonalDataShown ? (
        <Chat
          messagesPreload={preloadMessagesHandler}
          messagesLoading={isLoading}
          dialogId={dialogId?.data?.id}
          hasPreloadedMessages={messagesData?.data?.preload && !isPreload}
          showRequest={isAddYourDataVisible}
          isInSchedule={isInSchedule}
          isOnline={isOnline}
          isDialogActive={isDialogActive}
          messages={messages}
          members={membersData}
          channels={channels}
          isChatActive={isChatActive}
          sendMessage={sendMessage}
          sendMessageWithActiveDialog={(fn) => fn(dialogId)}
          onClick={hideYourDataHandler}
          onClose={toggleChat}
          onCancel={closeRequestHandler}
          showCloseButton
          onShowRequest={() => setRequestpersonalDataShown(true)}
        />
      ) : (
        <EnterYourData
          dialogId={dialogId?.data?.id}
          id={dialogId?.data?.id}
          fields={visitor_data_request}
          onClose={hideYourDataHandler}
        />
      )}
    </MainStyled>
  ) : (
    <CollapsedChat
      message={lastOperatorMessage}
      margins={margins}
      position={position}
      quickMessages={quickMessages}
      onDismissMessage={() => setLastOperatorMessage(null)}
      onOpen={toggleChat}
      onQuickMessage={sendQuickMessage}
    />
  );
};
